import { useId, useMemo, useState } from "react";

import { Check, ChevronDown, Search, X } from "lucide-react";

import { Badge } from "@components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@components/ui/popover";
import { ScrollArea } from "@components/ui/scroll-area";

import { cn } from "@utils/ui";

type Props = {
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
  /** Hint shown in the trigger when nothing is selected. */
  placeholder?: string;
  /** Placeholder for the in-popover search input. */
  searchPlaceholder?: string;
  /** Accessible name for the trigger (and, suffixed, the search input). */
  ariaLabel?: string;
  /**
   * Cap the number of individually-rendered pills in the trigger. Any
   * remainder collapses into a single muted "+N more" pill so a large
   * selection doesn't grow the trigger unbounded. Omit for no cap.
   */
  maxDisplay?: number;
};

/**
 * Searchable multi-select with a pill-style trigger.
 *
 * The trigger renders each selected value as a primary-50 pill (wrapping to new
 * rows) with its own remove affordance; empty = the `placeholder` hint. With
 * `maxDisplay`, only the first N pills render and the rest collapse into a muted
 * "+N more" pill. The popover is a searchable checkbox list with Select all /
 * Clear all shortcuts and a Done button that just closes it (selection applies
 * live as you tick).
 */
export const MultiSelect = ({
  options,
  selected,
  onChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  ariaLabel = "Filter",
  maxDisplay,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const listboxId = useId();

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  // Pills render in options order (not click order) so the trigger reads
  // consistently regardless of the order values were ticked.
  const selectedInOrder = useMemo(
    () => options.filter((o) => selectedSet.has(o)),
    [options, selectedSet]
  );

  // Split into the pills we render individually vs the overflow count.
  const visiblePills =
    maxDisplay != null ? selectedInOrder.slice(0, maxDisplay) : selectedInOrder;
  const overflowCount = selectedInOrder.length - visiblePills.length;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.toLowerCase().includes(q)) : options;
  }, [options, query]);

  const toggle = (value: string) =>
    onChange(
      selectedSet.has(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value]
    );

  const remove = (value: string) => onChange(selected.filter((v) => v !== value));

  const selectAll = () => onChange([...options]);
  const clearAll = () => onChange([]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-controls={listboxId}
          aria-expanded={open}
          aria-label={ariaLabel}
          className="flex w-full items-center justify-between gap-2 rounded-lg border-[1.5px] border-primary bg-background-white px-3 py-2 text-left shadow-[0px_2px_6px_0px_rgba(56,77,166,0.08)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
        >
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            {selectedInOrder.length === 0 ? (
              <span className="text-xs text-foreground-muted">{placeholder}</span>
            ) : (
              <>
                {visiblePills.map((c) => (
                  <Badge key={c} variant="primary" className="max-w-full">
                    <span className="break-words text-left">{c}</span>
                    {/* role=button span, not a <button>: this sits inside the
                        trigger <button>, and nesting buttons is invalid DOM. */}
                    <span
                      role="button"
                      tabIndex={-1}
                      aria-label={`Remove ${c}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(c);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          remove(c);
                        }
                      }}
                      className="flex size-2.5 shrink-0 cursor-pointer items-center justify-center rounded-full hover:bg-primary/20"
                    >
                      <X className="size-2.5" />
                    </span>
                  </Badge>
                ))}
                {overflowCount > 0 && (
                  <Badge variant="default">+{overflowCount} more</Badge>
                )}
              </>
            )}
          </span>
          <ChevronDown className="size-4 shrink-0 text-primary" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-[var(--radix-popover-trigger-width)] overflow-hidden rounded-xl p-0"
      >
        <div className="flex items-center gap-2 border-b border-border/60 p-3">
          <Search className="size-3.5 shrink-0 text-foreground-muted" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            aria-label={`Search ${ariaLabel}`}
            className="min-w-0 flex-1 bg-transparent text-xs text-foreground placeholder:text-foreground-muted focus-visible:outline-none"
          />
        </div>

        <ScrollArea className="h-[273px]" id={listboxId}>
          {filtered.length === 0 ? (
            <p className="px-4 py-6 text-center text-xs text-foreground-muted">
              No matches.
            </p>
          ) : (
            filtered.map((c) => {
              const checked = selectedSet.has(c);
              return (
                <button
                  key={c}
                  type="button"
                  role="option"
                  aria-selected={checked}
                  onClick={() => toggle(c)}
                  className="flex w-full items-center gap-1.5 bg-background-white px-4 py-3 text-left hover:bg-[#E8FDFF]/50"
                >
                  <span
                    className={cn(
                      "flex size-3.5 shrink-0 items-center justify-center rounded-[3px] border",
                      checked
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-foreground-muted/40 bg-background-white"
                    )}
                  >
                    {checked && <Check className="size-2.5" />}
                  </span>
                  <span
                    className={cn(
                      "min-w-0 flex-1 text-xs",
                      checked
                        ? "font-semibold text-primary"
                        : "font-normal text-foreground"
                    )}
                  >
                    {c}
                  </span>
                </button>
              );
            })
          )}
        </ScrollArea>

        <div className="flex items-center justify-between border-t border-border/60 p-3">
          <div className="flex items-center gap-3 text-xs font-medium">
            <button
              type="button"
              onClick={selectAll}
              className="text-primary hover:underline"
            >
              Select all
            </button>
            <span className="text-foreground-muted">·</span>
            <button
              type="button"
              onClick={clearAll}
              className="text-primary hover:underline"
            >
              Clear all
            </button>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-lg bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground hover:bg-primary-700"
          >
            Done
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
